'use client';

import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  user-select: none;
`;

const TrackHitArea = styled.div`
  padding: 6px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  outline: none;

  &:focus-visible > div {
    outline: 2px solid #00ff00;
    outline-offset: 2px;
  }
`;

const TrackBackground = styled.div`
  width: 100%;
  height: 4px;
  background: var(--card-border, rgba(255, 255, 255, 0.15));
  border-radius: 2px;
  position: relative;
  overflow: visible;
  transition: height 0.15s ease;

  ${TrackHitArea}:hover &,
  ${TrackHitArea}:active & {
    height: 6px;
  }
`;

const BufferFill = styled.div.attrs<{ $pct: number; $isBuffering: boolean }>((props) => ({
  style: {
    width: `${Math.min(Math.max(props.$pct, 0), 100)}%`,
    transition: props.$pct === 0 ? 'none' : 'width 0.3s ease-out',
  },
}))<{ $pct: number; $isBuffering: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 2px;
  background: rgba(0, 255, 0, 0.32);
  box-shadow: ${({ $pct }) => ($pct >= 99 ? '0 0 8px rgba(0, 255, 0, 0.3)' : 'none')};
  pointer-events: none;

  ${({ $isBuffering }) =>
    $isBuffering &&
    css`
      background: linear-gradient(
        90deg,
        rgba(0, 255, 0, 0.15) 0%,
        rgba(255, 45, 149, 0.45) 50%,
        rgba(0, 255, 0, 0.15) 100%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.4s infinite linear;
    `}
`;

const ProgressFill = styled.div.attrs<{ $pct: number }>((props) => ({
  style: {
    width: `${Math.min(Math.max(props.$pct, 0), 100)}%`,
    transition: props.$pct === 0 ? 'none' : 'width 0.1s linear',
  },
}))<{ $pct: number }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #00ff00 0%, #ff2d95 100%);
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    right: -5px;
    top: 50%;
    transform: translateY(-50%) scale(0);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 8px #ff2d95, 0 0 4px #00ff00;
    transition: transform 0.15s ease;
  }

  ${TrackHitArea}:hover &::after,
  ${TrackHitArea}:active &::after {
    transform: translateY(-50%) scale(1);
  }
`;

const TimeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.65rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.5));
  font-variant-caps: all-petite-caps;
  letter-spacing: 0.5px;
  margin-top: 1px;
`;

const BufferStatus = styled.span<{ $full: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $full }) => ($full ? '#00ff00' : '#ff2d95')};
  font-weight: 600;
  font-size: 0.6rem;
  letter-spacing: 0.5px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ $full }) => ($full ? '#00ff00' : '#ff2d95')};
    box-shadow: 0 0 6px ${({ $full }) => ($full ? '#00ff00' : '#ff2d95')};
    ${({ $full }) =>
      !$full &&
      css`
        animation: ${pulseDot} 1s infinite ease-in-out;
      `}
  }
`;

export function formatTime(ms: number): string {
  if (!ms || Number.isNaN(ms) || ms < 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

export interface ProgressBarProps {
  progress: number; // 0 - 100 percentage
  buffered?: number; // 0 - 100 buffer amount
  isBuffering?: boolean;
  currentTimeMs?: number;
  durationMs?: number;
  onSeek: (pct: number) => void;
}

/**
 * Interactive audio playback progress bar with separate visual buffer bar, scrubbing, and time display.
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  buffered = 0,
  isBuffering = false,
  currentTimeMs = 0,
  durationMs = 0,
  onSeek,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPct, setDragPct] = useState<number | null>(null);

  // Exact clamp on 0 - 100 scale (no faulty * 100 scaling on 0-1 values)
  const normalizedPct = dragPct !== null ? dragPct : Math.min(Math.max(progress, 0), 100);

  const normalizedBuffer = Math.min(Math.max(buffered, 0), 100);
  const isFullyBuffered = normalizedBuffer >= 98;

  const calculatePctFromEvent = useCallback((clientX: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return (x / rect.width) * 100;
  }, []);

  const triggerSeek = (pct: number) => {
    const clamped = Math.min(Math.max(pct, 0), 100);
    onSeek(clamped);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    const pct = calculatePctFromEvent(e.clientX);
    setDragPct(pct);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const pct = calculatePctFromEvent(e.clientX);
    setDragPct(pct);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    const pct = calculatePctFromEvent(e.clientX);
    setDragPct(null);
    triggerSeek(pct);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      triggerSeek(Math.min(normalizedPct + 5, 100));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      triggerSeek(Math.max(normalizedPct - 5, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      triggerSeek(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      triggerSeek(100);
    }
  };

  const displayCurrentMs =
    isDragging && durationMs > 0 && dragPct !== null
      ? (dragPct / 100) * durationMs
      : currentTimeMs || (durationMs > 0 ? (normalizedPct / 100) * durationMs : 0);

  return (
    <Container>
      <TrackHitArea
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onKeyDown={handleKeyDown}
        role="slider"
        aria-label="Track playback progress"
        aria-valuenow={Math.round(normalizedPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${formatTime(displayCurrentMs)} of ${formatTime(durationMs)}`}
        tabIndex={0}
      >
        <TrackBackground>
          <BufferFill
            $pct={isBuffering && normalizedBuffer === 0 ? 100 : normalizedBuffer}
            $isBuffering={isBuffering}
          />
          <ProgressFill $pct={normalizedPct} />
        </TrackBackground>
      </TrackHitArea>

      <TimeRow>
        <span>
          {formatTime(displayCurrentMs)}
          {isBuffering && (
            <BufferStatus $full={false} style={{ marginLeft: 8 }}>
              buffering...
            </BufferStatus>
          )}
          {!isBuffering && isFullyBuffered && durationMs > 0 && (
            <BufferStatus $full={true} style={{ marginLeft: 8 }}>
              buffered
            </BufferStatus>
          )}
        </span>
        <span>{durationMs > 0 ? formatTime(durationMs) : '--:--'}</span>
      </TimeRow>
    </Container>
  );
};

export default ProgressBar;
